import React, { useState, useCallback, useMemo } from 'react';
import Page from 'components/Page';
import CTTabs from 'components/CTTabs';
import * as LocationAction from 'actions/location-action';
import FileUploader from 'components/FileUploader';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DataDialog from 'components/dialogs/DataDialog';
import LocationForm from './location-form';
import PageHeader from 'global/PageHeader';
import {
  Box, Button, ButtonGroup, Chip, CircularProgress, 
  IconButton, Switch, Typography,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import Searchbar from 'global/Searchbar';
import Notificationbar from 'global/Notificationbar';
import FormDrawer from 'components/FormDrawer';
import FormElements from 'components/FormElements';
import ButtonCancel from 'components/ButtonCancel';
import ButtonSave from 'components/ButtonSave';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';

const TABS = ['ACTIVE', 'INACTIVE'];

const LocationPage = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { locations, searched, toast, isProcessing, save, update, search, changeStatus, resetState } = props;
  const formState = {
    location: {
      name: '',
      email: '',
      phone: '',
      street: '',
      city: '',
      state: '',
      zipcode: '',
      country: '',
      isActive: true,
    },
  };

  const [location, setLocation] = useState(formState.location);
  const [action, setAction] = useState('Save');
  const [tabIndex, setTabIndex] = useState(0);
  const [isLocationFormDrawerOpen, setIsLocationFormDrawerOpen] = useState(false);
  const [isFileLoaderDialogOpen, setIsFileLoaderDialogOpen] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchParameters, setSearchParameters] = useState({});
  const [submitForm, setSubmitForm] = useState(() => () => {});

  const executeSearch = useCallback((searchParams) => {
    if (searchParams && Object.entries(searchParams).length > 0) {
      const value = searchParams['name'];
      setIsSearchEnabled(true);
      setSearchParameters(searchParams);
      search(value);
    } else {
      setIsSearchEnabled(false);
      setSearchParameters({});
    }
  }, [search]);

  const createSearchChip = useCallback(() => {
    return Object.entries(searchParameters).map(([k, v]) => (
      <Chip
        key={k}
        label={v}
        onDelete={(evt) => deleteSearchChip(evt, k)}
        color='secondary'
        size='small'
      />
    ));
  }, [searchParameters]);

  const deleteSearchChip = useCallback((evt, key) => {
    const updatedParams = { ...searchParameters };
    delete updatedParams[key];
    executeSearch(updatedParams);
  }, [searchParameters, executeSearch]);

  const saveFormHandler = useCallback((locationData) => {
    save(locationData);
  }, [save]);

  const updateFormHandler = useCallback((locationData) => {
    update(locationData);
  }, [update]);

  const toggleFormDialog = useCallback(() => {
    setIsLocationFormDrawerOpen(prev => !prev);
  }, []);

  const closeDialog = useCallback(() => {
    setLocation(formState.location);
    setIsLocationFormDrawerOpen(false);
    setIsFileLoaderDialogOpen(false);
    setAction('Save');
    resetState('RESET_TOAST_STATE');
  }, [resetState]);

  const editRow = useCallback((row) => {
    setIsLocationFormDrawerOpen(true);
    setAction('Update');
    setLocation({ ...row, ...row.address });
  }, []);

  const toggleActiveStatus = useCallback((id) => {
    changeStatus(id);
    resetState('RESET_TOAST_STATE');
  }, [changeStatus, resetState]);

  const changeTab = useCallback((index) => {
    setTabIndex(index);
  }, []);

  const downloadTemplate = useCallback(() => {
    const filename = 'location_template.csv';
    const headers = ['name', 'email', 'phone', 'contact_name', 'street', 'city', 'state', 'country', 'zipcode', 'is_active'];
    let csvContent = 'data:text/csv;charset=utf-8,' + headers;
    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }, []);

  const downloadLocations = useCallback(() => {
    const filename = 'locations.csv';
    const csvData = [];
    const headers = ['Name', 'Email', 'Phone', 'Street', 'City', 'State', 'Country', 'Zipcode', 'Is Active'];
    csvData.push(headers);

    locations.forEach(item => {
      let arr = item && item.address
        ? [item.name, item.email, item.phone, item.address.street, item.address.city, item.address.state, item.address.country, item.address.zipcode, item.active]
        : [item.name, item.email, item.phone];
      csvData.push(arr);
    });

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvData.forEach(function (rowArray) {
      csvContent += rowArray.join(',') + '\r\n';
    });

    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }, [locations]);

  const columns = useMemo(() => [
    { field: 'id', headerName: 'Id' },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'phone', headerName: 'Phone', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'street', headerName: 'Street', flex: 1, valueGetter: (value, row) => row.address.street },
    { field: 'city', headerName: 'City', flex: 1, valueGetter: (value, row) => row.address.city },
    { field: 'state', headerName: 'State', flex: 1, valueGetter: (value, row) => row.address.state },
    { field: 'zip', headerName: 'Zip', flex: 1, valueGetter: (value, row) => row.address.zipcode },
    { field: 'country', headerName: 'Country', flex: 1, valueGetter: (value, row) => row.address.country },
    {
      field: 'status',
      headerName: 'Active',
      flex: 1,
      renderCell: (params) => (
        <Switch
          checked={params.row?.active}
          onChange={() => toggleActiveStatus(params.row.id)}
          color='secondary'
        />
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => editRow(params.row)}>
          <FTIcons.EditIcon
            width={20}
            height={20}
            fill={colors.primary.main}
          />
        </IconButton>
      )
    },
  ], [ toggleActiveStatus, closeDialog, editRow]);

  // filtered data
  const selectedTab = TABS[tabIndex];
  const flag = selectedTab === 'ACTIVE';
  const tableData = isSearchEnabled ? searched : locations;
  let filteredData = [];
  const statusCounts = [];

  if (tableData) {
    filteredData = tableData.filter(d => d.active === flag);
    statusCounts.push(
      { status: 'ACTIVE', count: tableData.filter(d => d.active).length },
      { status: 'INACTIVE', count: tableData.filter(d => !d.active).length }
    );
  }

  return (
    <Page title="Locations">
      <Box display={'flex'} justifyContent={'space-between'}>
        <PageHeader title='LOCATION' subTitle='Manage Your origin & destination' />
        <Searchbar
          searchType='SIMPLE'
          searchKey='name'
          searchParameters={searchParameters}
          fnOnSearchHandler={executeSearch}
        />
        <Notificationbar />
      </Box>
      <Box m='20px 0 0 0' height='75vh'>
        <Box style={{ margin: '10px 0' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Button variant='contained' color='primary' onClick={toggleFormDialog}>New Location</Button>
            {isProcessing && <CircularProgress style={{ margin: 'auto' }} />}
            {toast && <ToastHandler toast={toast} type={toast.type} />}
            <ButtonGroup variant='outlined' color='secondary'>
              <Button onClick={() => setIsFileLoaderDialogOpen(true)}>Import</Button>
              <Button onClick={downloadTemplate}>Template</Button>
              <Button onClick={downloadLocations}>Export</Button>
            </ButtonGroup>
          </Box>
        </Box>
        <Box display={'flex'}>
          <Box flex={1}>
            <CTTabs labels={TABS} counts={statusCounts} tabIndex={tabIndex} fnOnTabChangeHandler={changeTab} />
          </Box>
          {isSearchEnabled && (
            <Box flex={1}>
              <Typography
                variant='h5'
                style={{
                  letterSpacing: 1,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '2px'
                }}
              >
                {`${searched.length} records found`}
              </Typography>
              {createSearchChip()}
            </Box>
          )}
        </Box>
        <DataGrid rows={filteredData} columns={columns} rowHeight={40} disableSelectionOnClick />
      </Box>

      <FormDrawer
        open={isLocationFormDrawerOpen}
        title={`${action === 'Save' ? 'Create Location' : 'Edit Location'}`}
        toast={toast}
        isProcessing={isProcessing}
        handleClose={closeDialog}
        footer={
          <FormElements.FormFooter>
            <ButtonCancel action={closeDialog} />
            <ButtonSave title={action} action={() => submitForm()} />
          </FormElements.FormFooter>
        }
      >
        <LocationForm
          action={action}
          location={location}
          saveFormHandler={saveFormHandler}
          updateFormHandler={updateFormHandler}
          exposeSubmit={(submitFn) => setSubmitForm(() => submitFn)}
        />
      </FormDrawer>

      <DataDialog
        open={isFileLoaderDialogOpen}
        title='Import'
        toast={toast}
        isProcessing={isProcessing}
        onCloseHandler={closeDialog}
      >
        <FileUploader name={'location'} />
      </DataDialog>
    </Page>
  );
};

function mapStatesToProps(state) {
  const { locations, searched, isProcessing, toast } = state.locationReducer;
  return { locations, searched, isProcessing, toast };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(LocationAction, dispatch);
}

export default connect(mapStatesToProps, mapDispatchToProps)(LocationPage);